import { onFetchDealerCobrandingStatus, onFetchDealerCobrandingStatusByDealerId } from '../actions/actions.dealer';
import dynamicEntryValidateActions from '../actions/actions.validation.dynamic-entry';
import vehicleActions from '../actions/actions.vehicle';
import { DuplicateOfferModal, PreviousVehicle, VinModal } from '../interfaces/interface.landing';
import { VehiclePossibility } from '../interfaces/interface.vehicle';
import { getSuggestions } from '../services/service.banner';
import EventBus from '../services/service.eventBus';
import { fetchStates } from '../services/service.location';
import { getIsOptimizelyTrue } from '../services/service.optimizely';
import { fetchActiveAutotraderListing, fetchVehiclePossibilities, fetchVehicleTypicalMileage } from '../services/service.vehicle';
import { VehicleEligibility, fetchVehicleEligibility, fetchYearsEligibility } from '../services/services.eligibility';
import { state as cobrandingParamsState } from '../store/store.cobranding';
import { state as dynamicEntryState, onChange as onChangeDynamicEntryState } from '../store/store.dynamic-entry';
import { state as funnelComponentState } from '../store/store.funnel-component';
import { state as pageParamsState } from '../store/store.page-params';
import { DynamicEntryEvents, trackDynamicEntry, trackStartMyOfferError, trackStartMyOfferIneligible, trackStartMyOfferSuccess } from '../tracking/tracking.dynamic-entry';
import {
  DYNAMIC_ENTRY_TABS,
  DynamicEntryInstallationMode,
  DynamicEntryStep,
  LICENSE_PLATE_ERRORS,
  MSM_MILEAGE_ERRORS,
  VEHICLE_INFO_REQUIRED_BY_ANSWER_STATE,
  VIN_ERRORS,
  createFunnelUrl,
  getPreviousVehicleFromPossibilities,
  isLucidGroup,
  isYearEligible,
  isYearIneligibleForLPR,
  mapSuggestionsToValue,
} from '../utils/utils.dynamic-entry';
import { determineWorkflowForSuperComponent } from '../utils/utils.funnel-component';
import { DynamicEntryWorkflow, scrollTo, LANDING_FAQ } from '../utils/utils.landing';
import { DEFAULT } from '../utils/utils.landing-oem';

import { onStateFieldAnswered } from './actions.answers';

const onDynamicEntryInit = async (
  offerCode: string,
  dynamicEntryWorkflow: DynamicEntryWorkflow,
  dealerId: string,
  enableMakeModel: boolean,
  installationMode: DynamicEntryInstallationMode,
  tabList: Array<string>,
  defaultSelectedTab: string,
  vehicle: string,
  inputVin: string,
  mileage: number,
  dealerGroupId: string,
  cobrandingGroupId: string,
  modalInfo: string
) => {
  addListenerForDynamicEntryStates();
  onReceivedParams({
    offerCode,
    dynamicEntryWorkflow,
    dealerId,
    enableMakeModel,
    installationMode,
    tabList,
    defaultSelectedTab,
    vehicle,
    inputVin,
    mileage,
    dealerGroupId,
    cobrandingGroupId,
  });
  await onOptimizelyIneligibleVehicleCheck();

  if (!funnelComponentState.isSuperComponent && (dealerGroupId || cobrandingGroupId)) {
    await fetchCoBrandingInfo(dealerGroupId, cobrandingGroupId, dealerId);
  }

  if (inputVin) {
    await onFindByVin();
  }
  onFetchYearsEligibility();
  onModalChange(modalInfo);
  onSubscribeToEventBus();
};

const onFindByVin = async (type: string = 'manual'): Promise<void> => {
  try {
    dynamicEntryState.mileageRangeValidated = false;
    await onFindByVinSuccess();
    trackStartMyOffer(type);
  } catch (error) {
    onFindByVinFail(error);
  }
};

const fetchCoBrandingInfo = async (dealerGroupId: string, cobrandingGroupId: string, dealerId: string) => {
  if (dealerGroupId) {
    await onFetchDealerCobrandingStatusByDealerId(dealerGroupId, cobrandingGroupId, dealerId);
  } else {
    await onFetchDealerCobrandingStatus(cobrandingGroupId);
  }
};

const trackStartMyOffer = (type: string) => {
  trackDynamicEntry(type === 'manual' ? DynamicEntryEvents.START_MY_OFFER : DynamicEntryEvents.START_MY_OFFER_SCAN);
};

const onFindByVinSuccess = async (): Promise<void> => {
  const { inputVin } = dynamicEntryState;
  EventBus.publish('FindByVin', { vin: inputVin });
  const isValid = dynamicEntryValidateActions.onValidateVinEntry({ isNotFound: false });
  let isIneligible = false;

  if (isValid) {
    dynamicEntryState.isFetching = true;
    dynamicEntryState.vin = inputVin;
    const isValidVin = await onFetchVehiclePossibilities(inputVin);

    if (!isValidVin) {
      dynamicEntryValidateActions.onValidateVinEntry({ isNotFound: true });
    } else {
      if (dynamicEntryState.vehiclePossibilities?.length === 1) {
        isIneligible = !(await checkVehicleEligibility(dynamicEntryState.trimId));
      }

      const notQualify = isIneligible || !dynamicEntryState.allowIneligibleVehicleSubmission || !isYearEligible(dynamicEntryState.yearId);
      if (notQualify) {
        trackStartMyOfferIneligible({ vm: true });
        dynamicEntryState.activeStep = DynamicEntryStep.NotQualify;
      } else {
        const isVinActiveDealer = await onFetchActiveAutotraderListing(inputVin);
        if (!isVinActiveDealer) {
          dynamicEntryValidateActions.onValidateVinActiveDealerListingEntry();
        }
        dynamicEntryState.activeStep = DynamicEntryStep.MSM;
        trackStartMyOfferSuccess('cta::start-my-offer-success::vin');
      }
    }

    dynamicEntryState.isFetching = false;
    onChangeDifferentVehicle();
  }

  EventBus.publish('FindByVinSuccess', { isValid, isIneligible });
};

const onFindByVinFail = (error) => {
  EventBus.publish('FindByVinFail', error);
};

const onFindByYmm = async (isPrimaryInput = true) => {
  try {
    await onFindByYmmSuccess(isPrimaryInput);
  } catch (error) {
    onFindByYmmFail(error);
  }
};

const onFindByYmmSuccess = async (isPrimaryInput = true) => {
  const { vehicleId, inputYmm } = dynamicEntryState;
  EventBus.publish('FindByYmm', { vehicleId, inputYmm });
  if (isPrimaryInput) {
    trackDynamicEntry(DynamicEntryEvents.START_MY_OFFER);
  } else {
    trackDynamicEntry(DynamicEntryEvents.GO_YMM_MANUAL);
  }

  if (vehicleId !== 0 && inputYmm !== '') {
    onStoreUpdate({ hasYmmError: false });
    dynamicEntryValidateActions.onValidateYmmEntry();
    dynamicEntryState.ymmSuggestions = [];
    dynamicEntryState.isFetching = true;
    const eligible = await checkVehicleEligibility(vehicleId);
    dynamicEntryState.allowIneligibleVehicleSubmission = eligible;
    if (eligible && isYearEligible(dynamicEntryState.yearId)) {
      dynamicEntryState.vin = '';
      dynamicEntryState.inputVin = '';
      dynamicEntryState.licensePlateNumber = '';
      dynamicEntryState.licensePlateState = '';
      dynamicEntryState.activeStep = DynamicEntryStep.MSM;
      trackStartMyOfferSuccess('cta::start-my-offer-success::ymm');
    } else {
      trackStartMyOfferIneligible({ vm: true });
      dynamicEntryState.activeStep = DynamicEntryStep.NotQualify;
    }

    dynamicEntryState.isFetching = false;
  } else {
    onStoreUpdate({ hasYmmError: true });
    dynamicEntryValidateActions.onValidateYmmEntry();
  }

  onChangeDifferentVehicle();
  EventBus.publish('FindByYmmSuccess', {});
};

const onFindByYmmFail = (error) => {
  EventBus.publish('FindByYmmFail', error);
};

const checkVehicleEligibility = async (trimId: number = 0): Promise<boolean> => {
  try {
    return await onFetchVehicleEligibilitySuccess(trimId);
  } catch (error) {
    onFetchVehicleEligibilityFail(error);
    return false;
  }
};

const onFetchVehicleEligibilitySuccess = async (trimId: number = 0): Promise<boolean> => {
  if (trimId === 0) {
    return false;
  }

  EventBus.publish('FetchVehicleEligibility', trimId);
  const eligibilityRec: VehicleEligibility = await fetchVehicleEligibility(trimId);
  const { optimizelyIneligibleVehicleIsActive } = dynamicEntryState;
  const isAssignedOrCobranding = isAssignedOrCobrandingFlow();

  const shouldBypassIneligibility = optimizelyIneligibleVehicleIsActive && isAssignedOrCobranding;
  const isEligible = eligibilityRec.isEligible || shouldBypassIneligibility;

  EventBus.publish('FetchVehicleEligibilitySuccess', eligibilityRec);
  return isEligible;
};

const onFetchVehicleEligibilityFail = (error) => {
  EventBus.publish('FetchVehicleEligibilityFail', error);
};

const onFindByPlate = async () => {
  try {
    await onFindByPlateSuccess();
  } catch (error) {
    onFindByPlateFail(error);
  }
};

const onFindByPlateSuccess = async () => {
  const { licensePlateNumber, licensePlateState } = dynamicEntryState;
  EventBus.publish('FindByPlate', { licensePlateNumber, licensePlateState });
  dynamicEntryState.isFetching = true;
  let isIneligible = false;
  const vins = await vehicleActions.onFetchVinByPlate(licensePlateNumber, licensePlateState);
  let hasError = !vins || vins.length === 0;
  if (!hasError) {
    const isFoundVin = await onFetchVehiclePossibilities(vins[0]);
    hasError = !isFoundVin;
  }

  if (!hasError && dynamicEntryState.vehiclePossibilities.length === 1) {
    isIneligible = !(await checkVehicleEligibility(dynamicEntryState.trimId));
  }

  const notQualify = isIneligible || !dynamicEntryState.allowIneligibleVehicleSubmission || !isYearEligible(dynamicEntryState.yearId);

  if (hasError) {
    dynamicEntryValidateActions.onValidatePlateEntry();
  } else if (notQualify) {
    trackStartMyOfferIneligible({ vm: true });
    dynamicEntryState.activeStep = DynamicEntryStep.NotQualify;
  } else {
    const isVinActiveDealer = await onFetchActiveAutotraderListing(vins[0]);
    if (!isVinActiveDealer) {
      dynamicEntryValidateActions.onValidateVinActiveDealerListingEntry();
    }

    dynamicEntryState.activeStep = DynamicEntryStep.MSM;
    trackStartMyOfferSuccess('cta::start-my-offer-success::lp');
  }

  dynamicEntryState.isFetching = false;
  onChangeDifferentVehicle();
  EventBus.publish('FindByPlateSuccess', { hasError });
};

const onFindByPlateFail = (error) => {
  EventBus.publish('FindByPlateFail', error);
};

const onGetYmmSuggestions = (inputYmm = '') => {
  try {
    onGetYmmSuggestionsSuccess(inputYmm);
  } catch (error) {
    onGetYmmSuggestionsFail(error);
  }
};

const onGetYmmSuggestionsSuccess = (inputYmm = '') => {
  EventBus.publish('GetYmmSuggestions', inputYmm);
  onStoreUpdate({ inputYmm, hasYmmError: false });
  dynamicEntryValidateActions.onValidateYmmEntry();
  if (inputYmm !== '') {
    dynamicEntryState.makeId = 0;
    dynamicEntryState.make = '';
    dynamicEntryState.model = '';
    dynamicEntryState.modelId = 0;
    dynamicEntryState.trimId = 0;
    dynamicEntryState.vehicleId = 0;
    dynamicEntryState.yearId = 0;
    dynamicEntryState.ymmSuggestions = [];

    getSuggestions(inputYmm).then((results) => {
      if (results) {
        dynamicEntryState.ymmSuggestions = results.filter((r) => r.yearId >= dynamicEntryState.yearRange.min && r.yearId <= dynamicEntryState.yearRange.max);
      }
    });
  }

  EventBus.publish('GetYmmSuggestionsSuccess', dynamicEntryState.ymmSuggestions);
};

const onGetYmmSuggestionsFail = (error) => {
  EventBus.publish('GetYmmSuggestionsFail', error);
};

const onFetchActiveAutotraderListing = async (vin: string): Promise<boolean> => {
  try {
    EventBus.publish('FetchActiveAutotraderListing', vin);
    dynamicEntryState.vin = vin;
    const vinActiveDealerResult = await fetchActiveAutotraderListing(vin);

    const isVinActiveDealer =
      vinActiveDealerResult &&
      vinActiveDealerResult.totalResultCount &&
      vinActiveDealerResult.totalResultCount > 0 &&
      !vinActiveDealerResult.privateSeller &&
      !pageParamsState.isAdmin;
    const vinActiveDealerUrl = vinActiveDealerResult && vinActiveDealerResult.autoListingUrl ? vinActiveDealerResult.autoListingUrl : '';

    EventBus.publish('FetchActiveAutotraderListingSuccess', vin);

    if (isVinActiveDealer) {
      trackStartMyOfferError('vin_active_dealer_listing_error');
      dynamicEntryState.vinActiveDealerUrl = vinActiveDealerUrl;

      return false;
    }

    return true;
  } catch (error) {
    EventBus.publish('FetchActiveAutotraderListingFail', error);
    return false;
  }
};

const onFetchVehiclePossibilities = async (vin: string): Promise<boolean> => {
  if (dynamicEntryState.invalidVins.includes(vin)) {
    return false;
  }

  try {
    return await onFetchVehiclePossibilitiesSuccess(vin);
  } catch (error) {
    onFetchVehiclePossibilitiesFail(error, vin);
    return false;
  }
};

const onFetchVehiclePossibilitiesSuccess = async (vin: string): Promise<boolean> => {
  EventBus.publish('FetchVehiclePossibilities', vin);
  dynamicEntryState.vin = vin;
  dynamicEntryState.inputVin = vin;
  const fetchByVin = await fetchVehiclePossibilities(vin);
  const vehicles: VehiclePossibility[] = fetchByVin.possibilities;
  EventBus.publish('FetchVehiclePossibilitiesSuccess', vin);

  if (!vehicles || vehicles.length === 0) {
    trackStartMyOfferError('vin_error');
    return false;
  }

  const previousVehicle = getPreviousVehicleFromPossibilities(vehicles, dynamicEntryState.trim);
  const vehicle = previousVehicle || vehicles[0];
  const { autoCheckData } = fetchByVin;

  dynamicEntryState.vehiclePossibilities = buildOptions(vehicles);
  dynamicEntryState.vehiclePossibilitiesData = vehicles;
  dynamicEntryState.autoCheckData = autoCheckData;
  const isAssignedOrCobranding = isAssignedOrCobrandingFlow();

  // For assigned or cobranding non dr, allow ineligible vehicle submission
  const shouldBypassIneligibility = dynamicEntryState.optimizelyIneligibleVehicleIsActive && isAssignedOrCobranding;
  const isAutoCheckEligible = (fetchByVin.isAutoCheckEligible === undefined || fetchByVin.isAutoCheckEligible) && !isAssignedOrCobranding;

  dynamicEntryState.allowIneligibleVehicleSubmission = shouldBypassIneligibility || isAutoCheckEligible;

  const shouldGetTrim = vehicles.length === 1 || !!previousVehicle;
  setVehicleOptions(vehicle, shouldGetTrim);
  if (autoCheckData && !dynamicEntryState.allowIneligibleVehicleSubmission) {
    trackStartMyOfferIneligible(autoCheckData);
    dynamicEntryState.activeStep = DynamicEntryStep.NotQualify;
    return false;
  }
  return true;
};

const isAssignedOrCobrandingFlow = () => {
  const { isAssignedOffer, isCobranding } = dynamicEntryState;

  // US1678534 - Allow Ineligible VIN and mileage over 300k
  // If it is assigned link, allow ineligible for both super component and dynamic entry
  // If it is cobranding, allow ineligible when the allowIneligibleVehicle is turning on in Dynamic Entry

  const isAssigned = isAssignedOffer && !isCobranding;
  const allowIneligibleForCobranding = isCobranding && cobrandingParamsState.allowIneligibleVehicle;
  const isAssignedOrCobranding = allowIneligibleForCobranding || isAssigned;
  return isAssignedOrCobranding;
};

const setVehicleOptions = (vehicle, setTrim) => {
  dynamicEntryState.yearId = vehicle.year.id;
  dynamicEntryState.makeId = parseInt(vehicle.make.id.toString());
  dynamicEntryState.make = vehicle.make.displayName.toString();
  dynamicEntryState.modelId = vehicle.model.id;
  dynamicEntryState.model = vehicle.model.displayName.toString();
  dynamicEntryState.transmissionId = vehicle.transmissions[0]?.id;
  dynamicEntryState.engineId = vehicle.engines[0]?.id;
  dynamicEntryState.drivetrainId = vehicle.drivetrains[0]?.id;
  dynamicEntryState.genericOptions = vehicle.genericOptions.map((option) => option.genericName);
  dynamicEntryState.colorId = vehicle.colors && vehicle.colors[0].id;

  if (setTrim) {
    dynamicEntryState.trimId = vehicle.trim.id;
    dynamicEntryState.trim = vehicle.trim.displayName.toString();
    dynamicEntryState.vehicleId = vehicle.trim.id;
  }
  setInputYmm();
};

const buildOptions = (options: VehiclePossibility[]) => {
  if (!options) {
    return [];
  }

  return options.map((opts: VehiclePossibility) => ({
    label: `${opts.trim.displayName}`,
    value: `${opts.trim.id}`,
  }));
};

const onFetchVehiclePossibilitiesFail = (error, vin: string) => {
  dynamicEntryState.invalidVins = [...dynamicEntryState.invalidVins, vin];
  trackStartMyOfferError('vin_error');
  EventBus.publish('FetchVehiclePossibilitiesFail', error);
};

const onYmmSelected = (vID) => {
  try {
    onYmmSelectedSuccess(vID);
  } catch (error) {
    onYmmSelectedFail(error);
  }
};

const onYmmSelectedSuccess = (vID) => {
  EventBus.publish('YmmSelected', vID);
  const selectedSuggestion = dynamicEntryState.ymmSuggestions.find((s) => s.vehicleId === vID);
  const { makeId, makeName, modelId, modelName, yearId, vehicleId, trimId, trimName } = selectedSuggestion;
  dynamicEntryState.makeId = makeId;
  dynamicEntryState.make = makeName;
  dynamicEntryState.model = modelName;
  dynamicEntryState.modelId = modelId;
  dynamicEntryState.trimId = trimId;
  dynamicEntryState.trim = trimName;
  dynamicEntryState.vehicleId = vehicleId;
  dynamicEntryState.yearId = yearId;
  dynamicEntryState.inputYmm = mapSuggestionsToValue(dynamicEntryState.ymmSuggestions).find((s) => s.value === vehicleId).label;
  dynamicEntryState.ymmSuggestions = [];
  dynamicEntryState.hasYmmError = false;
  if (dynamicEntryState.currentSelectedTab === 'ymm') {
    dynamicEntryState.vehiclePossibilities = [];
  }
  dynamicEntryValidateActions.onValidateYmmEntry();
  EventBus.publish('YmmSelectedSuccess', {});
};

const onYmmSelectedFail = (error) => {
  EventBus.publish('YmmSelectedFail', error);
};

const onStoreUpdate = (props = {}) => {
  Object.entries(props).forEach(([name, value]) => {
    dynamicEntryState[name] = value;
  });
};

const validTabs = Object.values(DYNAMIC_ENTRY_TABS);
const getTabHeaders = (tabList: string[], enableMakeModel: boolean) => {
  const headers = [];
  tabList.forEach((tab) => {
    if (tab === DYNAMIC_ENTRY_TABS.VIN) {
      headers.push({
        key: 'vin',
        label: 'VIN',
      });
    }

    if (tab === DYNAMIC_ENTRY_TABS.PLATE) {
      headers.push({
        key: 'plate',
        label: 'License Plate',
      });
    }

    if (tab === DYNAMIC_ENTRY_TABS.YMM && enableMakeModel) {
      headers.push({
        key: 'ymm',
        label: 'Make Model',
      });
    }
  });

  return headers;
};

const onVehicleChanged = () => {
  resetDynamicEntryState();

  setPreviousVehicle();
};

const resetDynamicEntryState = () => {
  dynamicEntryState.activeStep = DynamicEntryStep.FormSelector;
  dynamicEntryState.isComponentRendering = true;
  // Proceed without Instant Cash Offer resets after Change Vehicle
  dynamicEntryState.styleError = '';
  dynamicEntryState.mileageError = '';
  dynamicEntryState.hasMileageError = false;
  dynamicEntryState.isFetching = false;
  dynamicEntryState.hasVinActiveAutotraderListingError = false;

  // Reset trimId when change vehicle from VIN with only mileage
  dynamicEntryState.trimId = 0;
  dynamicEntryState.hasStyleError = false;

  // Reset Duplicate Modal
  dynamicEntryState.duplicateOfferModal = { ...dynamicEntryState.duplicateOfferModal, open: false };
};

const setPreviousVehicle = () => {
  dynamicEntryState.previousVehicle = { vin: dynamicEntryState.vin, vehicleId: dynamicEntryState.vehicleId, entry: dynamicEntryState.currentSelectedTab } as PreviousVehicle;
};

const onChangeDifferentVehicle = () => {
  try {
    onChangeDifferentVehicleSuccess();
  } catch (error) {
    onChangeDifferentVehicleFail(error);
  }
};

const onChangeDifferentVehicleSuccess = () => {
  const { previousVehicle, vehicleId } = dynamicEntryState;
  if (!previousVehicle) {
    return;
  }

  EventBus.publish('ChangeDifferentVehicle', {});
  const vin = dynamicEntryState.inputVin || dynamicEntryState.vin;
  if ((vin && (previousVehicle?.vin || previousVehicle?.entry === DYNAMIC_ENTRY_TABS.YMM) && vin !== previousVehicle.vin) || vehicleId !== previousVehicle.vehicleId) {
    dynamicEntryState.mileage = undefined;
    dynamicEntryState.colorId = 0;
    dynamicEntryState.color = '';
    setPreviousVehicle();
  }

  EventBus.publish('ChangeDifferentVehicleSuccess', {});
};

const onChangeDifferentVehicleFail = (error) => {
  EventBus.publish('AutoCompleteYmmFail', error);
};

const onTabSelected = (key: string) => {
  dynamicEntryState.currentSelectedTab = key;
  onSetHasSecondaryYmmOption();
};

const onSetHasSecondaryYmmOption = () => {
  const { enableMakeModel, currentSelectedTab: selectedTab } = dynamicEntryState;
  const errorMessage = selectedTab === DYNAMIC_ENTRY_TABS.VIN ? dynamicEntryState.vinError : dynamicEntryState.lpError;
  dynamicEntryState.hasYmmOption2 =
    !enableMakeModel &&
    ((selectedTab === DYNAMIC_ENTRY_TABS.VIN && errorMessage === VIN_ERRORS.VIN_NOT_FOUND_ERROR_2_OPTIONS) ||
      (selectedTab === DYNAMIC_ENTRY_TABS.PLATE && errorMessage === LICENSE_PLATE_ERRORS.PLATE_NOT_FOUND_UNASSIGNED));
};

const onReceivedParams = ({
  offerCode,
  dynamicEntryWorkflow,
  enableMakeModel,
  dealerId,
  installationMode,
  tabList,
  defaultSelectedTab,
  vehicle,
  inputVin,
  mileage,
  dealerGroupId,
  cobrandingGroupId,
}) => {
  try {
    onReceivedParamsSuccess({
      offerCode,
      dynamicEntryWorkflow,
      enableMakeModel,
      dealerId,
      installationMode,
      tabList,
      defaultSelectedTab,
      vehicle,
      inputVin,
      mileage,
      dealerGroupId,
      cobrandingGroupId,
    });
  } catch (error) {
    onReceivedParamsFail(error);
  }
};

const onReceivedParamsSuccess = ({
  offerCode,
  dynamicEntryWorkflow,
  enableMakeModel,
  dealerId,
  installationMode,
  tabList,
  defaultSelectedTab,
  vehicle,
  inputVin,
  mileage,
  dealerGroupId,
  cobrandingGroupId,
}) => {
  EventBus.publish('ReceivedParams', { offerCode, dynamicEntryWorkflow, enableMakeModel, dealerId, installationMode, tabList, defaultSelectedTab, inputVin, mileage });
  if (!Object.values(DynamicEntryInstallationMode).includes(installationMode)) {
    throw 'Invalid installation-mode.';
  }

  const isTabInvalid = tabList.some((tab) => !validTabs.includes(tab)) || tabList[0] === '';
  if (isTabInvalid) {
    throw 'Invalid tab-list.';
  }

  if (!tabList.includes(defaultSelectedTab)) {
    throw 'Invalid default-selected-tab.';
  }
  const workflows = [DynamicEntryWorkflow.OEM, DynamicEntryWorkflow.Assigned, DynamicEntryWorkflow.DigitalRetail];
  const { isSuperComponent } = funnelComponentState;
  const superComponentWorkflow = determineWorkflowForSuperComponent(cobrandingParamsState);

  dynamicEntryState.offerCode = offerCode ?? '';
  dynamicEntryState.shouldShowIneligibleButton = true;
  dynamicEntryState.dynamicEntryWorkflow = isSuperComponent && !isLucidGroup(cobrandingGroupId) ? superComponentWorkflow : dynamicEntryWorkflow;
  dynamicEntryState.enableMakeModel = enableMakeModel;
  dynamicEntryState.dealerId = dealerId;
  dynamicEntryState.installationMode = installationMode;
  dynamicEntryState.defaultSelectedTab = defaultSelectedTab;
  dynamicEntryState.currentSelectedTab = defaultSelectedTab;
  dynamicEntryState.inputVin ||= inputVin;
  dynamicEntryState.mileage ||= mileage;
  dynamicEntryState.cobrandingGroupId = cobrandingGroupId;
  dynamicEntryState.isCobranding = cobrandingGroupId.length > 0;

  if (workflows.includes(dynamicEntryWorkflow) && tabList.length > 2) {
    dynamicEntryState.tabList = cobrandingParamsState.isRequireVinPlate ? ['vin', 'plate'] : DEFAULT.TAB_LIST;
  } else {
    dynamicEntryState.tabList = tabList;
  }

  dynamicEntryState.tabHeaders = getTabHeaders(dynamicEntryState.tabList, enableMakeModel);

  if (cobrandingGroupId?.length > 0) {
    dynamicEntryState.isAssignedOffer = dealerGroupId?.length > 0;
  } else {
    dynamicEntryState.isAssignedOffer = dealerId?.length > 0;
  }

  if (vehicle) {
    try {
      const vehicleObject = JSON.parse(vehicle);
      if (Object.keys(vehicleObject).length > 0) {
        console.log('Parsed vehicle object:', vehicleObject);
        const trimByName =
          vehicleObject.trim &&
          vehicleObject.trims &&
          !vehicleObject.trimId &&
          vehicleObject.trims.filter((trim) => trim.displayName === decodeURIComponent(vehicleObject.trim.trim()));
        dynamicEntryState.vin = vehicleObject.vin || dynamicEntryState.vin;
        dynamicEntryState.inputVin = vehicleObject.vin || dynamicEntryState.inputVin;
        dynamicEntryState.yearId = vehicleObject.year || dynamicEntryState.yearId;
        dynamicEntryState.year = vehicleObject.year || dynamicEntryState.year;
        dynamicEntryState.make = vehicleObject.make || dynamicEntryState.make;
        dynamicEntryState.model = vehicleObject.model || dynamicEntryState.model;
        dynamicEntryState.trim = vehicleObject.trim || dynamicEntryState.trim;
        dynamicEntryState.trimId = vehicleObject.trimId || dynamicEntryState.trimId || (trimByName && trimByName[0]?.trimId);
        dynamicEntryState.mileage = vehicleObject.mileage || dynamicEntryState.mileage;
        setInputYmm();

        if (dynamicEntryState.inputYmm?.trim()) {
          if (vehicleObject.vin) {
            dynamicEntryState.currentSelectedTab = DYNAMIC_ENTRY_TABS.VIN;
          } else if (tabList.includes(DYNAMIC_ENTRY_TABS.YMM)) {
            dynamicEntryState.currentSelectedTab = DYNAMIC_ENTRY_TABS.YMM;
          }
        }

        if ((pageParamsState.isAdmin && dynamicEntryState.vin && dynamicEntryState.mileage) || (vehicleObject.inviteId && dynamicEntryState.vin)) {
          dynamicEntryState.activeStep = DynamicEntryStep.MSM;
        }

        if (vehicleObject.trims?.length > 1) {
          dynamicEntryState.vehiclePossibilities = vehicleObject.trims.map(({ displayName, trimId }) => ({
            label: displayName,
            value: trimId.toString(),
          }));
        }
      }
    } catch (error) {}
  }
  EventBus.publish('ReceivedParamsSuccess', {});
  trackDynamicEntry(DynamicEntryEvents.LOAD_ICO_HOME);
};

const onReceivedParamsFail = (error) => {
  EventBus.publish('ReceivedParamsFail', error);
};

const onModalChange = (modalInfo: string) => {
  if (modalInfo) {
    try {
      const modalInfoObject = JSON.parse(modalInfo);
      if (Object.keys(modalInfoObject).length > 0) {
        const { vinModalInfo, duplicateInfo } = modalInfoObject;
        if (vinModalInfo) {
          dynamicEntryState.vinModal = { open: vinModalInfo.vinModal, mismatch: vinModalInfo.misMatch };
        } else {
          resetVinModal();
        }

        if (duplicateInfo) {
          dynamicEntryState.duplicateOfferModal = {
            open: !!duplicateInfo.userId && !!duplicateInfo.offerId,
            offerId: duplicateInfo.offerId,
            userId: duplicateInfo.userId,
          };
        } else {
          resetDuplicateOfferModal();
        }
      }
    } catch (error) {}
  } else {
    resetVinModal();
    resetDuplicateOfferModal();
  }
};

const onVinModalClosed = () => {
  resetVinModal();
};

const resetVinModal = () => {
  dynamicEntryState.vinModal = {} as VinModal;
};

const onDuplicateOfferModalReopen = () => {
  if (pageParamsState.isAdmin && dynamicEntryState.duplicateOfferModal && dynamicEntryState.duplicateOfferModal.offerId && dynamicEntryState.duplicateOfferModal.userId) {
    dynamicEntryState.duplicateOfferModal = { ...dynamicEntryState.duplicateOfferModal, open: true };
    return dynamicEntryState.duplicateOfferModal.open;
  }
};

const onDuplicateOfferModalClosed = () => {
  dynamicEntryState.duplicateOfferModal = { ...dynamicEntryState.duplicateOfferModal, open: false };
};

const resetDuplicateOfferModal = () => {
  dynamicEntryState.duplicateOfferModal = {} as DuplicateOfferModal;
};

const onAutoCompleteYmm = async () => {
  try {
    onAutoCompleteYmmSuccess();
  } catch (error) {
    onAutoCompleteYmmFail(error);
  }
};

const onAutoCompleteYmmSuccess = async () => {
  EventBus.publish('AutoCompleteYmm', {});
  if (!dynamicEntryState.inputYmm) {
    return;
  }

  const results = await getSuggestions(dynamicEntryState.inputYmm);
  dynamicEntryState.ymmSuggestions = results;
  const vehicle = results.find((vehicle) => vehicle.vehicleId === dynamicEntryState.vehicleId);
  if (vehicle) {
    onYmmSelected(vehicle.vehicleId);
  }

  EventBus.publish('AutoCompleteYmmSuccess', vehicle);
};

const onAutoCompleteYmmFail = (error) => {
  EventBus.publish('AutoCompleteYmmFail', error);
};

const onFetchYearsEligibility = () => {
  try {
    return onFetchYearsEligibilitySuccess();
  } catch (error) {
    onFetchYearsEligibilityFail(error);
  }
};

const onFetchYearsEligibilitySuccess = async () => {
  EventBus.publish('FetchYearsEligibility', {});

  dynamicEntryState.yearRange = await fetchYearsEligibility();
  EventBus.publish('FetchYearsEligibilitySuccess', {});
};

const onFetchYearsEligibilityFail = (error) => {
  EventBus.publish('FetchYearsEligibilityFail', error);
};

const onStatesLoad = async (): Promise<void> => {
  try {
    await onStatesLoadSuccess();
  } catch (error) {
    onStatesLoadFail(error);
  }
};

const onStatesLoadSuccess = async (): Promise<void> => {
  if (dynamicEntryState.states && dynamicEntryState.states.length > 0) {
    return;
  }

  EventBus.publish('StatesLoad', {});
  const stateData = await fetchStates();
  dynamicEntryState.states = stateData.map((state) => ({ label: state.abbreviation, value: state.abbreviation }));
  EventBus.publish('StatesLoadSuccess', stateData);
};

const onStatesLoadFail = (error) => {
  EventBus.publish('StatesLoadFail', error);
};

const onPlateInputChanged = (props = {}) => {
  try {
    onPlateInputChangedSuccess(props);
  } catch (error) {
    onPlateInputChangedFail(error);
  }
};

const onPlateInputChangedSuccess = (props = {}) => {
  EventBus.publish('PlateInputChanged', props);
  onStoreUpdate(props);
  dynamicEntryValidateActions.onValidatePlateEntry({ hasChanged: true });
  EventBus.publish('PlateInputChangedSuccess', {});
};

const onPlateInputChangedFail = (error) => {
  EventBus.publish('PlateInputChangedFail', error);
};

const onStyleChanged = (trimId = 0) => {
  onStoreUpdate({ trimId });
  if (!isNaN(trimId) && dynamicEntryState.vehiclePossibilitiesData?.length > 0) {
    const vehicle = dynamicEntryState.vehiclePossibilitiesData.find((x) => x.trim.id == trimId);
    setVehicleOptions(vehicle, true);
  }
  dynamicEntryValidateActions.onValidateStyle();
};

const onMileageChanged = (mileage = 0) => {
  onStoreUpdate({ mileage });
  dynamicEntryValidateActions.onValidateMileage();
};

const onMileageSubmit = async () => {
  try {
    await onMileageSubmitSuccess();
  } catch (error) {
    onMileageSubmitFail(error);
  }
};

const onMileageSubmitSuccess = async () => {
  const { trimId, mileage, vehiclePossibilities, hasMultipleTrims } = dynamicEntryState;
  EventBus.publish('MileageSubmit', { trimId, mileage });
  const hasMileageError = !dynamicEntryValidateActions.onValidateMileage();
  const hasStyleError = !dynamicEntryValidateActions.onValidateStyle();
  if (!hasStyleError && !hasMileageError) {
    dynamicEntryState.isFetching = true;
    const isEligible = await checkVehicleEligibility(trimId);
    // // Check year Eligible for Landing Page Redesigned
    const isYearEligibleForLPR = !isYearIneligibleForLPR(dynamicEntryState.yearId);

    if (isEligible && isYearEligibleForLPR) {
      if (hasMultipleTrims) {
        dynamicEntryState.trimId = trimId;
        dynamicEntryState.trim = vehiclePossibilities.find((v) => v.value === trimId.toString()).label;
        dynamicEntryState.vehicleId = trimId;
      }

      dynamicEntryState.mileage = mileage;
      dynamicEntryState.activeStep = DynamicEntryStep.ThankYou;

      onDynamicEntryNextAction();
      dynamicEntryState.isFetching = false;
    } else {
      const trackData = !isYearEligibleForLPR ? { year: true } : { notActualMileage: true };
      trackStartMyOfferIneligible(trackData);
      dynamicEntryState.activeStep = DynamicEntryStep.NotQualify;
    }
  } else {
    trackStartMyOfferError(hasMultipleTrims ? 'style_error' : 'mileage_error');
  }

  if (hasMultipleTrims) {
    trackDynamicEntry(DynamicEntryEvents.STYLE_MILEAGE_CONTINUE);
  } else {
    trackDynamicEntry(DynamicEntryEvents.MILEAGE_CONTINUE);
  }
  EventBus.publish('MileageSubmitSuccess', { trimId, mileage, hasStyleError, hasMileageError });
};

const onMileageSubmitFail = (error) => {
  EventBus.publish('MileageSubmitFail', error);
};

const onOptimizelyIneligibleVehicleCheck = async () => {
  try {
    await onOptimizelyCheckSuccess();
  } catch (error) {
    onOptimizelyCheckFail(error);
  }
};

const onOptimizelyCheckSuccess = async () => {
  EventBus.publish('OptimizelyCheck', 'EnableIneligibleVehicles');

  const ret = await getIsOptimizelyTrue('enableineligiblevehicles', 'ineligibleVehiclesIsEnabled');
  dynamicEntryState.optimizelyIneligibleVehicleIsActive = ret.data.variableValue;

  EventBus.publish('OptimizelyIneligibleVehicleCheckSuccess', ret);
};

const onOptimizelyCheckFail = (error) => {
  EventBus.publish('OptimizelyIneligibleVehicleCheckFail', error);
};

const onDynamicEntryNextAction = () => {
  VEHICLE_INFO_REQUIRED_BY_ANSWER_STATE.forEach((key) => {
    onStateFieldAnswered('vehicle', key, dynamicEntryState[key]);
  });

  dynamicEntryState.furyUrl = createFunnelUrl({ ...dynamicEntryState });

  switch (dynamicEntryState.installationMode) {
    case DynamicEntryInstallationMode.Redirect:
      window.location.href = dynamicEntryState.furyUrl;
      break;
    case DynamicEntryInstallationMode.Offsite:
      window.open(dynamicEntryState.furyUrl);
      break;
    case DynamicEntryInstallationMode.PopUp:
      dynamicEntryState.shouldOpenFunnelModal = true;
      break;
    case DynamicEntryInstallationMode.Integrated:
      const dynamicEntryVehicleData = {
        vin: dynamicEntryState.vin,
        yearId: dynamicEntryState.yearId,
        make: dynamicEntryState.make,
        makeId: dynamicEntryState.makeId,
        model: dynamicEntryState.model,
        modelId: dynamicEntryState.modelId,
        trim: dynamicEntryState.trim,
        trimId: dynamicEntryState.trimId,
        mileage: dynamicEntryState.mileage,
        transmissionId: dynamicEntryState.transmissionId,
        engineId: dynamicEntryState.engineId,
        drivetrainId: dynamicEntryState.drivetrainId,
        genericOptions: dynamicEntryState.genericOptions,
        autoCheckData: dynamicEntryState.autoCheckData,
        colorId: dynamicEntryState.colorId,
      };
      EventBus.publish('LandingNextClick', dynamicEntryVehicleData);
      break;
  }
};

const onTabWillRender = (tab) => {
  EventBus.publish('TabWillRender', { tab });
};

const onTabDidRender = (tab) => {
  EventBus.publish('TabDidRender', { tab });
};

const onProceedWithoutICO = (hasMileageError = false, hasStyleError = false, hasVinActiveAutotraderListingError = false) => {
  let reason = '';
  if (hasMileageError) {
    reason = 'Mileage was over 300000.';
  } else if (hasStyleError) {
    reason = 'Style is not eligible on this vehicle.';
  } else if (hasVinActiveAutotraderListingError) {
    reason = 'Vin has an active dealer listing on autotrader.';
  }
  EventBus.publish('ProceedWithoutICO', {
    reason,
    year: dynamicEntryState.yearId,
    make: dynamicEntryState.makeId,
    model: dynamicEntryState.modelId,
    trim: dynamicEntryState.trimId,
    vin: dynamicEntryState.vin,
  });
  trackDynamicEntry(DynamicEntryEvents.PROCEED_WITHOUT_ICO);
};

const onTrackWhereIsMyVIN = () => {
  trackDynamicEntry(DynamicEntryEvents.WHERE_IS_MY_VIN);
};

const onSubscribeToEventBus = () => {
  EventBus.subscribe('VinModalClosed', onVinModalClosed);
  EventBus.subscribe('DuplicateOfferModalClosed', onDuplicateOfferModalClosed);
};

const onTryAgainClick = async () => {
  if (dynamicEntryState.currentSelectedTab === DYNAMIC_ENTRY_TABS.PLATE) {
    await onFindByPlate();
  } else {
    await onFindByVin();
  }
};

const onTrackStartMyOfferLP = () => {
  trackDynamicEntry(DynamicEntryEvents.START_MY_OFFER);
  if (!dynamicEntryState.licensePlateNumber || !dynamicEntryState.licensePlateState) {
    trackStartMyOfferError('lp_error_lp_none');
  }
};

const setInputYmm = () => {
  const { yearId, make, model, trim } = dynamicEntryState;
  dynamicEntryState.inputYmm = [yearId, make, model, trim].filter((v) => v).join(' ');
};

const addListenerForDynamicEntryStates = () => {
  onChangeDynamicEntryState('vehiclePossibilities', (value) => {
    dynamicEntryState.hasMultipleTrims = value && value.length > 1;
    dynamicEntryState.shouldShowStyleSelection = dynamicEntryState.currentSelectedTab !== 'ymm' && dynamicEntryState.hasMultipleTrims;
  });

  onChangeDynamicEntryState('currentSelectedTab', (value) => {
    dynamicEntryState.shouldShowStyleSelection = value !== 'ymm' && dynamicEntryState.hasMultipleTrims;
  });
};

const onValidateMileageRange = async (): Promise<boolean> => {
  const { mileage, mileageRangeValidated, autoCheckData, trimId } = dynamicEntryState;
  const autoCheckMiles = (autoCheckData as { miles?: number })?.miles;

  if (!trimId || !mileage || mileageRangeValidated || typeof autoCheckMiles !== 'number') {
    dynamicEntryState.mileageError = '';
    return true;
  }

  dynamicEntryState.mileageRangeValidated = true;

  if (mileage < autoCheckMiles) {
    dynamicEntryState.mileageError = MSM_MILEAGE_ERRORS.MSM_MILEAGE_LESS_THAN_AUTOCHECK;
    return false;
  }

  if (mileage > autoCheckMiles) {
    await onFetchVehicleTypicalMileage(trimId);
    if (dynamicEntryState.typicalMileage && mileage > dynamicEntryState.typicalMileage * 1.2) {
      dynamicEntryState.mileageError = MSM_MILEAGE_ERRORS.MSM_MILEAGE_GREATER_THAN_AUTOCHECK;
      return false;
    }
  }

  dynamicEntryState.mileageError = '';
  return true;
};

const isMileageWarning = (): boolean => {
  return (
    dynamicEntryState.mileageError === MSM_MILEAGE_ERRORS.MSM_MILEAGE_GREATER_THAN_AUTOCHECK ||
    dynamicEntryState.mileageError === MSM_MILEAGE_ERRORS.MSM_MILEAGE_LESS_THAN_AUTOCHECK
  );
};

const onFetchVehicleTypicalMileage = async (trimId: number) => {
  try {
    return await onFetchVehicleTypicalMileageSuccess(trimId);
  } catch (error) {
    onFetchVehicleTypicalMileageFail(error);
  }
};

const onFetchVehicleTypicalMileageSuccess = async (trimId: number) => {
  EventBus.publish('FetchVehicleTypicalMileage', {});
  dynamicEntryState.typicalMileage = await fetchVehicleTypicalMileage(trimId);
  EventBus.publish('FetchVehicleTypicalMileageSuccess', { trimId });
};

const onFetchVehicleTypicalMileageFail = (error) => {
  EventBus.publish('FetchVehicleTypicalMileageFail', error);
};

const handleScrollToTop = () => {
  // Check if we're in an iframe
  if (window.parent && window.parent !== window) {
    EventBus.publish('ScrollToTop', {});
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const handleScrollTo = (elementName: string) => {
  // Check if we're in an iframe
  if (window.parent && window.parent !== window) {
    EventBus.publish('ScrollTo', { elementName });
  } else {
    scrollTo(LANDING_FAQ);
  }
};

const actions = {
  onDynamicEntryInit,
  onFindByVin,
  onFindByPlate,
  onFindByYmm,
  onGetYmmSuggestions,
  onYmmSelected,
  onReceivedParams,
  onStatesLoad,
  onStoreUpdate,
  onTabSelected,
  onSetHasSecondaryYmmOption,
  onPlateInputChanged,
  onVehicleChanged,
  onStyleChanged,
  onMileageChanged,
  onMileageSubmit,
  onTabWillRender,
  onTabDidRender,
  onProceedWithoutICO,
  onTrackWhereIsMyVIN,
  onTrackStartMyOfferLP,
  onAutoCompleteYmm,
  onFetchYearsEligibility,
  onOptimizelyIneligibleVehicleCheck,
  onModalChange,
  onDuplicateOfferModalReopen,
  onSubscribeToEventBus,
  onTryAgainClick,
  onValidateMileageRange,
  onFetchVehicleTypicalMileage,
  isAssignedOrCobrandingFlow,
  isMileageWarning,
  handleScrollToTop,
  handleScrollTo,
};

export { actions };
export default actions;
